import { AudiologistCatalogColumnDTO, ConnectedAudiologistListItemDTO } from "@bvhi/backend-api";
import { Box, GridColDef, Typography } from "@bvhi/ui-base";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ActionCells } from "../../../../../../components/datagrid/cells/action-cells";
import { UserStatusCell } from "../../../users/list/components/datagrid/user-status-cell";
import { AudiologistCatalogDownloadCell } from "../components/datagrid/audiologist-catalog-download-cell";
import { AudiologistDetailLinkCell } from "../components/datagrid/audiologist-detail-link-cell";
import { useAudiologistsSortParams } from "./use-audiologists-sort-params";
import { AudiologistsPrimaryChannelsCell } from "../components/datagrid/audiologists-primary-channels-cell";
import { BooleanChip } from "../../../../../../components/boolean-chip";
import { hasFeatureEnabled } from "../../../../../../utils/env-helper";

export const useAudiologistsDataGridDefinitions = (
  handleDelete: (userId: string) => void,
  catalogs: AudiologistCatalogColumnDTO[] = []
) => {
  const { t } = useTranslation(["audiologists", "customers"]);
  const { isFieldSortable } = useAudiologistsSortParams();
  const orderApiEnabled = useMemo(() => hasFeatureEnabled("ENABLE_ORDER_API"), []);

  const columns: GridColDef<ConnectedAudiologistListItemDTO>[] = useMemo(() => {
    const catalogColumns: GridColDef<ConnectedAudiologistListItemDTO>[] = catalogs.map((catalog) => ({
      field: `catalogDownload_${catalog.id}`,
      headerName: catalog.name,
      minWidth: 150,
      width: 180,
      maxWidth: 240,
      sortable: false,
      renderCell: (params) => {
        const download = params.row.catalogDownloads?.find((item) => item.catalogId === catalog.id);
        return <AudiologistCatalogDownloadCell download={download} />;
      },
    }));

    return [
      {
        field: "chainNumber",
        headerName: t("audiologists:audiologists.datagrid.chain.number"),
        minWidth: 150,
        width: 175,
        maxWidth: 225,
        sortable: isFieldSortable("chainNumber"),
        renderCell: (params) => (
          <Box alignItems="center" display="flex" sx={{ width: "100%", height: "100%" }}>
            <AudiologistDetailLinkCell audiologistId={params.row.id ? params.row.id : "-1"} label={params.row.chainNumber} />
          </Box>
        ),
      },
      {
        field: "organisationName",
        headerName: t("audiologists:audiologists.datagrid.organisation.name"),
        minWidth: 150,
        width: 200,
        maxWidth: 250,
        align: "center",
        sortable: isFieldSortable("organisationName"),
        renderCell: (params) => (
          <Box alignItems="center" display="flex" sx={{ width: "100%", height: "100%" }}>
            <Typography>{params.value}</Typography>
          </Box>
        ),
      },
      {
        field: "email",
        headerName: t("audiologists:audiologists.datagrid.email"),
        minWidth: 150,
        width: 200,
        maxWidth: 250,
        sortable: isFieldSortable("email"),
        renderCell: (params) => (
          <Box alignItems="center" display="flex" sx={{ width: "100%", height: "100%" }}>
            <Typography>{params.value}</Typography>
          </Box>
        ),
      },
      {
        field: "lastName",
        headerName: t("audiologists:audiologists.datagrid.name"),
        minWidth: 150,
        width: 200,
        maxWidth: 250,
        valueGetter: (_value, row) => `${row.lastName}, ${row.firstName}`,
        sortable: isFieldSortable("lastName"),
        renderCell: (params) => (
          <Box alignItems="center" display="flex" sx={{ width: "100%", height: "100%" }}>
            <Typography>{params.value}</Typography>
          </Box>
        ),
      },
      {
        field: "status",
        headerName: t("audiologists:audiologists.datagrid.status"),
        minWidth: 100,
        width: 200,
        maxWidth: 300,
        sortable: isFieldSortable("status"),
        renderCell: (params) => (
          <Box alignItems="center" display="flex" sx={{ width: "100%", height: "100%" }}>
            <UserStatusCell data={params.row.status} hasLoggedIn={true} />
          </Box>
        ),
      },
      ...catalogColumns,
      ...(orderApiEnabled
        ? ([
            {
              field: "hasOrderToken",
              headerName: t("audiologists:audiologists.datagrid.has.order.token"),
              minWidth: 100,
              width: 190,
              maxWidth: 200,
              sortable: isFieldSortable("hasOrderToken"),
              renderCell: (params) => (
                <Box alignItems="center" display="flex" sx={{ width: "100%", height: "100%" }}>
                  <BooleanChip
                    falseLabel={t("customers:customers.no.content.card.available.values.NEIN")}
                    trueLabel={t("customers:customers.no.content.card.available.values.JA")}
                    value={params.row.hasOrderToken}
                  />
                </Box>
              ),
            },
            {
              field: "channels",
              headerName: t("audiologists:audiologists.datagrid.channels"),
              minWidth: 100,
              width: 300,
              maxWidth: 500,
              sortable: isFieldSortable("channels"),
              renderCell: (params) => (
                <Box alignItems="center" display="flex" py={1} sx={{ width: "100%", height: "100%" }}>
                  <AudiologistsPrimaryChannelsCell channels={params.row.channels} />
                </Box>
              ),
            },
          ] as GridColDef<ConnectedAudiologistListItemDTO>[])
        : []),
      {
        field: "id",
        headerName: t("audiologists:audiologists.datagrid.actions"),
        flex: 1,
        minWidth: 125,
        headerClassName: "datagrid-sticky-right",
        cellClassName: "datagrid-sticky-right",
        align: "right",
        headerAlign: "right",
        sortable: isFieldSortable("id"),
        resizable: false,
        renderCell: (params) => (
          <ActionCells
            deletePermission="supplier:audiologist:delete"
            detailParams={{ audiologistId: params.row.id }}
            detailPermission="supplier:audiologist:update"
            detailRoute="/app/audiologists/$audiologistId"
            handleDelete={handleDelete}
            id={params.row.id}
            showDeleteAction={true}
            showDetailAction={true}
          />
        ),
      },
    ];
  }, [t, isFieldSortable, orderApiEnabled, handleDelete, catalogs]);

  return { columns };
};
